import axios from 'axios';

import {
  TApplicantsListRequest,
  TApplicationDemoRequest,
  TAppliedProgramsListRequest,
  TArchiveProgramRequest,
  TCloseProgramRequest,
  TCreateApplicationRequest,
  TCreateProgramRequest,
  TEditProgramMembersRequest,
  TGetApplicationRequest,
  TGetProgramRequest,
  TProgramMembersListRequest,
  TProgramListRequest,
  TUpdateProgramRequest,
  TPromoteApplicationRequest,
  TGetProgramMembersRequest,
  TUploadProgramFileRequest,
  TUploadApplicationFileRequest,
  TUploadFileToS3Request,
  TGetProgramDocumentRequest,
  TRespondToKeyMemberSelectionRequest
} from './apiEndpoint.types.ts';
import { APIResponse, BaseAPIService } from './base.api.service';

export class ProgramAPIService extends BaseAPIService {
  private createProgramPath: string = '/program/create';
  private createListProgramPath: string = '/user/program/create';
  private createEventPath: string = '/global-events/create';
  private updateEventPath: string = '/global-events/update';
  private updateProgramPath: string = '/program/update';
  private programListPath: string = '/program/list';
  private activeProgramListPath: string = '/program/active/list';
  private eventListPath: string = '/global-events/list';
  private listedProgramPath: string = '/user/program/list';
  private getProgramPath: string = '/program/get';
  private getEventPath: string = '/global-events/get';
  private createApplicationPath: string = '/program/application/create';
  private createEventApplicationPath: string = '/global-events/application/create';
  private closeProgramPath: string = '/program/close';
  private archiveProgramPath: string = '/program/archive';
  private applicantsListPath: string = '/program/application/list';
  private eventApplicantsListPath: string = '/global-events/application/list';
  private getApplicationPath: string = '/program/application/get';
  private editProgramMembersPath: string = 'program/members/edit';
  private createApplicationDemoPath: string = 'program/application/demo/create';
  private promoteApplicationPath: string = 'program/application/promote';
  private appliedProgramsListPath: string = 'program/list/applied';
  private programsMemberListPath: string = 'program/members/list';
  private keyMembersListPath: string = 'company/key-members/list';
  private respondToKeyMemberSelectionPath: string = 'company/key-members/respond-to-request';
  private getPresignePath: string = 'program/banner/upload';
  private getPresigneEventPath: string = 'global-events/banner/upload';
  private uploadProgramFilePath: string = 'program/document/create';
  private uploadEventFilePath: string = 'event/document/create';
  private uploadApplicationFilePath: string = 'program/application/document/create';
  private uploadEventApplicationFilePath: string = 'event/application/document/create';
  private getDocumentPath: string = 'program/documents/get';
    static getPresignedUrl: any;

  constructor() {
    // Program api service has a different endpoint from base service.
    super();
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_API_PROGRAM_ENDPOINT
    });
    this.configureInterceptors();
  }

  public async createProgram<TCreateProgramResponse>(
    createProgramRequest: TCreateProgramRequest
  ): Promise<APIResponse<TCreateProgramResponse>> {
    const createProgramResponse: APIResponse<TCreateProgramResponse> = await this.post(
      this.createProgramPath,
      createProgramRequest,
      true
    );
    return createProgramResponse;
  }

  public async createListProgram(
    createProgramRequest
  ) {
    const createProgramResponse = await this.post(
      this.createListProgramPath,
      createProgramRequest,
      true
    );
    return createProgramResponse;
  }

  public async createEvent<TCreateProgramResponse>(
    createEventRequest
  ): Promise<APIResponse<any>> {
    const createEventResponse: APIResponse<any> = await this.post(
      this.createEventPath,
      createEventRequest,
      true
    );
    return createEventResponse;
  }

  public async updateEvent(
    createEventRequest
  ) {
    const createEventResponse = await this.patch(
      this.updateEventPath,
      createEventRequest,
      true
    );
    return createEventResponse;
  }

  public async updateProgram(
    updateProgramRequest
  ) {
    const updateProgramResponse = await this.patch(
      this.updateProgramPath,
      updateProgramRequest,
      true
    );
    return updateProgramResponse;
  }
  // public async createProgram<TCreateProgramResponse>(
  //   createProgramRequest: TCreateProgramRequest
  // ): Promise<APIResponse<TCreateProgramResponse>> {
  //   const createProgramResponse: APIResponse<TCreateProgramResponse> = await this.post(
  //     this.createProgramPath,
  //     createProgramRequest,
  //     true
  //   );
  //   return createProgramResponse;
  // }

  public async programList<TProgramListResponse>(
    programListRequest: TProgramListRequest
  ): Promise<APIResponse<TProgramListResponse>> {
    const programListResponse: APIResponse<TProgramListResponse> = await this.get(
      this.programListPath,
      true,
      programListRequest
    );
    return programListResponse;
  }

  public async allActiveProgramList<TProgramListResponse>(
    programListRequest: TProgramListRequest
  ): Promise<APIResponse<TProgramListResponse>> {
    const programListResponse: APIResponse<TProgramListResponse> = await this.get(
      this.activeProgramListPath,
      true,
      programListRequest
    );
    return programListResponse;
  }

  public async eventList(
    eventRequest
  ): Promise<APIResponse<any>> {
    const eventListResponse = await this.get(
      this.eventListPath,
      true,
      eventRequest
    );
    return eventListResponse;
  }

  public async listedProgramList(
    programRequest
  ): Promise<APIResponse<any>> {
    const listedProgramResponse = await this.get(
      this.listedProgramPath,
      true,
      programRequest
    );
    return listedProgramResponse;
  }

  public async getProgram<TGetProgramResponse>(
    getProgramRequest: TGetProgramRequest
  ): Promise<APIResponse<TGetProgramResponse>> {
    const getProgramResponse: APIResponse<TGetProgramResponse> = await this.get(
      this.getProgramPath,
      true,
      getProgramRequest
    );
    return getProgramResponse;
  }

  public async getEvent<TGetProgramExternalUserResponse>(
    getEventRequest: any
  ): Promise<APIResponse<TGetProgramExternalUserResponse>> {
    const getEventResponse: APIResponse<TGetProgramExternalUserResponse> = await this.get(
      this.getEventPath,
      false,
      getEventRequest
    );
    return getEventResponse;
  }

  public async createApplication<TCreateApplicationResponse>(
    createApplicationRequest: TCreateApplicationRequest
  ): Promise<APIResponse<TCreateApplicationResponse>> {
    const createApplicationResponse: APIResponse<TCreateApplicationResponse> = await this.post(
      this.createApplicationPath,
      createApplicationRequest,
      true
    );
    return createApplicationResponse;
  }

  public async createExternalEventApplication(
    createApplicationRequest
  ) {
    const { authorizationToken, ...payload } = createApplicationRequest;
    const createApplicationResponse =
      await this.post(this.createEventApplicationPath, payload, false, false, authorizationToken);
    // await this.post(this.createApplicationPath, payload, false, false);
    return createApplicationResponse;
  }

  public async closeProgram<TCloseProgramResponse>(
    closeProgramRequest: TCloseProgramRequest
  ): Promise<APIResponse<TCloseProgramResponse>> {
    const closeProgramResponse: APIResponse<TCloseProgramResponse> = await this.post(
      this.closeProgramPath,
      closeProgramRequest,
      true
    );
    return closeProgramResponse;
  }

  public async archiveProgram<TArchiveProgramResponse>(
    archiveProgramRequest: TArchiveProgramRequest
  ): Promise<APIResponse<TArchiveProgramResponse>> {
    const archiveProgramResponse: APIResponse<TArchiveProgramResponse> = await this.post(
      this.archiveProgramPath,
      archiveProgramRequest,
      true
    );
    return archiveProgramResponse;
  }

  public async applicantsList<TApplicantsListResponse>(
    applicantsListRequest: TApplicantsListRequest
  ): Promise<APIResponse<TApplicantsListResponse>> {
    const applicantsListResponse: APIResponse<TApplicantsListResponse> = await this.get(
      this.applicantsListPath,
      true,
      applicantsListRequest
    );
    return applicantsListResponse;
  }

  public async eventApplicantsList<TApplicantsListResponse>(
    applicantsListRequest: TApplicantsListRequest
  ): Promise<APIResponse<TApplicantsListResponse>> {
    const applicantsListResponse: APIResponse<TApplicantsListResponse> = await this.get(
      this.eventApplicantsListPath,
      true,
      applicantsListRequest
    );
    return applicantsListResponse;
  }

  public async getApplication<TGetApplicationResponse>(
    getApplicationRequest: TGetApplicationRequest
  ): Promise<APIResponse<TGetApplicationResponse>> {
    const getApplicationResponse: APIResponse<TGetApplicationResponse> = await this.get(
      this.getApplicationPath,
      true,
      getApplicationRequest
    );
    return getApplicationResponse;
  }

  public async editProgramMembers<TEditProgramMembersResponse>(
    editProgramMembersRequest: TEditProgramMembersRequest
  ): Promise<APIResponse<TEditProgramMembersResponse>> {
    const editProgramMembersResponse: APIResponse<TEditProgramMembersResponse> = await this.post(
      this.editProgramMembersPath,
      editProgramMembersRequest,
      true
    );
    return editProgramMembersResponse;
  }

  public async createApplicationDemo<TApplicationDemoResponse>(
    createApplicationDemoRequest: TApplicationDemoRequest
  ): Promise<APIResponse<TApplicationDemoResponse>> {
    const createApplicationDemoResponse: APIResponse<TApplicationDemoResponse> = await this.post(
      this.createApplicationDemoPath,
      createApplicationDemoRequest,
      true
    );
    return createApplicationDemoResponse;
  }

  public async promoteApplication<TPromoteApplicationResponse>(
    promoteApplicationRequest: TPromoteApplicationRequest
  ): Promise<APIResponse<TPromoteApplicationResponse>> {
    const promoteApplicationResponse: APIResponse<TPromoteApplicationResponse> = await this.post(
      this.promoteApplicationPath,
      promoteApplicationRequest,
      true
    );
    return promoteApplicationResponse;
  }

  public async appliedProgramsList<TAppliedProgramsListResponse>(
    appliedProgramsListRequest: TAppliedProgramsListRequest
  ): Promise<APIResponse<TAppliedProgramsListResponse>> {
    const appliedProgramsListResponse: APIResponse<TAppliedProgramsListResponse> = await this.get(
      this.appliedProgramsListPath,
      true,
      appliedProgramsListRequest
    );
    return appliedProgramsListResponse;
  }

  public async programMembersList<TProgramMembersListResponse>(
    programMembersListRequest: TProgramMembersListRequest
  ): Promise<APIResponse<TProgramMembersListResponse>> {
    const programMembersListResponse: APIResponse<TProgramMembersListResponse> = await this.get(
      this.programsMemberListPath,
      true,
      programMembersListRequest
    );
    return programMembersListResponse;
  }

  public async keyMembersList<TGetProgramKeyMemberResponse>(
    keyMembersListRequest: TGetProgramMembersRequest
  ): Promise<APIResponse<TGetProgramKeyMemberResponse>> {
    const keyMembersListResponse: APIResponse<TGetProgramKeyMemberResponse> = await this.get(
      this.keyMembersListPath,
      true,
      keyMembersListRequest
    );
    return keyMembersListResponse;
  }

  public async uploadProgramFile<TUploadProgramFileResponse>(
    uploadProgramFileRequest: TUploadProgramFileRequest
  ): Promise<APIResponse<TUploadProgramFileResponse>> {
    const uploadProgramFileResponse: APIResponse<TUploadProgramFileResponse> = await this.post(
      this.uploadProgramFilePath,
      uploadProgramFileRequest,
      true
    );
    return uploadProgramFileResponse;
  }

  public async uploadEventFile(
    uploadEventFileRequest
  ) {
    const uploadEventFileResponse = await this.post(
      this.uploadEventFilePath,
      uploadEventFileRequest,
      true
    );
    return uploadEventFileResponse;
  }

  public async uploadApplicationFile<TUploadApplicationFileResponse>(
    uploadApplicationFileRequest: TUploadApplicationFileRequest
  ): Promise<APIResponse<TUploadApplicationFileResponse>> {
    const uploadApplicationFileResponse: APIResponse<TUploadApplicationFileResponse> =
      await this.post(this.uploadApplicationFilePath, uploadApplicationFileRequest, true);
    return uploadApplicationFileResponse;
  }

  public async uploadEventApplicationFile<TUploadApplicationFileResponse>(
    uploadApplicationFileRequest: TUploadApplicationFileRequest
  ): Promise<APIResponse<TUploadApplicationFileResponse>> {
    const uploadApplicationFileResponse: APIResponse<TUploadApplicationFileResponse> =
      await this.post(this.uploadEventApplicationFilePath, uploadApplicationFileRequest, false);
    return uploadApplicationFileResponse;
  }

  public async uploadFileToS3<TUploadFileToS3Response>(
    uploadFileToS3Request: TUploadFileToS3Request
  ): Promise<APIResponse<TUploadFileToS3Response>> {
    const { data, includeAuthorizationHeaders = true } = uploadFileToS3Request;
    const uploadFileToS3Response: APIResponse<TUploadFileToS3Response> = await this.put(
      uploadFileToS3Request.s3Key,
      data,
      includeAuthorizationHeaders
    );
    return uploadFileToS3Response;
  }

  public async getDocument<TGetProgramDocumentResponse>(
    getDocumentRequest: TGetProgramDocumentRequest
  ): Promise<APIResponse<TGetProgramDocumentResponse>> {
    const documentResponse: APIResponse<TGetProgramDocumentResponse> = await this.get(
      this.getDocumentPath,
      true,
      getDocumentRequest
    );
    return documentResponse;
  }

  public async respondToKeyMemberSelection<TRespondToKeyMemberSelectionResponse>(
    respondToKeyMemberSelectionRequest: TRespondToKeyMemberSelectionRequest
  ): Promise<APIResponse<TRespondToKeyMemberSelectionResponse>> {
    const respondToKeyMemberSelectionResponse: APIResponse<TRespondToKeyMemberSelectionResponse> =
      await this.post(
        this.respondToKeyMemberSelectionPath,
        respondToKeyMemberSelectionRequest,
        true
      );
    return respondToKeyMemberSelectionResponse;
  }

  public async getPresignedUrl(
    presignedRequest
  ) {
    const presignedResponse =
      await this.get(
        this.getPresignePath,
        true,
        presignedRequest
      );
    return presignedResponse;
  }

  public async getPresignedEventUrl(
    presignedEventRequest
  ) {
    const presignedEventResponse =
      await this.get(
        this.getPresigneEventPath,
        true,
        presignedEventRequest
      );
    return presignedEventResponse;
  }
}
